'use client';

import { motion } from 'framer-motion';
import { CTA } from '@/components/CTA/CTA';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';
const fadeInUp = {
	initial: { opacity: 0, y: 20 },
	animate: { opacity: 1, y: 0 },
	transition: { duration: 0.5 },
};

const staggerContainer = {
	animate: {
		transition: {
			staggerChildren: 0.1,
		},
	},
};

export const FeatureGrid = ({
	heading,
	sub_heading,
	features = [],
	CTAs,
	isFirst = false,
}: components['schemas']['DynamicZoneFeatureGridComponent'] & {
	isFirst?: boolean;
}) => {
	const SectionHeading = getSectionMotionHeading(isFirst);
	return (
		<section className={cn('feature-grid-section py-16 md:py-24')}>
			<noscript>
				<style>{`
					.feature-grid-section h2[style],
					.feature-grid-section h3[style],
					.feature-grid-section p[style],
					.feature-grid-section li[style],
					.feature-grid-section div[style*='opacity'],
					.feature-grid-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="container mx-auto px-4">
				{/* Header */}
				<motion.div
					className="mb-8 text-center"
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, margin: '-100px' }}
					variants={staggerContainer}>
					<SectionHeading
						variants={fadeInUp}
						className="mb-4 text-3xl font-bold md:text-4xl">
						{heading}
					</SectionHeading>
					{sub_heading && (
						<motion.p
							variants={fadeInUp}
							className="mx-auto max-w-2xl text-lg text-muted-foreground">
							{sub_heading}
						</motion.p>
					)}
				</motion.div>

				{/* Features Grid */}
				<motion.div
					variants={staggerContainer}
					initial="initial"
					whileInView="animate"
					viewport={{ once: true, margin: '-100px' }}
					className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
					{features.map((feature, index) => (
						<motion.div
							key={index}
							variants={fadeInUp}
							whileHover={{
								y: -5,
								transition: { duration: 0.2 },
							}}
							className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm transition-shadow hover:shadow-md">
							<motion.div
								initial={{ opacity: 0, x: -20 }}
								animate={{ opacity: 1, x: 0 }}
								transition={{ delay: 0.2 }}
								className="flex min-h-[60px] items-center gap-4">
								<motion.div
									whileHover={{ scale: 1.1 }}
									className="text-3xl text-primary">
									{feature.icon}
								</motion.div>
								<h3 className="text-xl font-semibold">
									{feature.title}
								</h3>
							</motion.div>

							<motion.ul
								initial={{ opacity: 0 }}
								animate={{ opacity: 1 }}
								transition={{ delay: 0.3 }}
								className="mt-4 space-y-2">
								{feature.features?.map((point, pointIndex) => (
									<motion.li
										key={pointIndex}
										initial={{ opacity: 0, x: -20 }}
										animate={{ opacity: 1, x: 0 }}
										transition={{ delay: 0.1 * pointIndex }}
										className="flex items-start gap-2">
										<motion.div
											whileHover={{ scale: 1.1 }}
											className={cn(
												'mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-blue-500 transition-all duration-300',
												// 'min-h-[24px] min-w-[24px]',
											)}
											aria-hidden="true">
											<svg
												className="h-3 w-3 text-white"
												fill="none"
												viewBox="0 0 24 24"
												stroke="currentColor"
												width="12"
												height="12">
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M5 13l4 4L19 7"
												/>
											</svg>
										</motion.div>
										<span className="text-muted-foreground">
											{point.text}
										</span>
									</motion.li>
								))}
							</motion.ul>
						</motion.div>
					))}
				</motion.div>
				<motion.div
					variants={staggerContainer}
					className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
					<CTA CTAs={CTAs || []} />
				</motion.div>
			</div>
		</section>
	);
};
